import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element's content is actually clipped horizontally.
 *
 * Compares `scrollWidth` to `clientWidth` and keeps the answer in sync with a
 * `ResizeObserver`, so a tooltip can be enabled only while the text really is
 * cut off — and disappear again when the container grows or the value changes.
 *
 * Shared by `TruncatedText` (a plain string) and `TruncatedNode` (already
 * rendered content), so the two never drift on how truncation is detected.
 *
 * @param deps - re-measure when these change (e.g. the rendered value).
 */
export function useIsTruncated<T extends HTMLElement>(
  deps: unknown[] = []
): { ref: RefObject<T | null>; isTruncated: boolean } {
  const ref = useRef<T | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-supplied re-measure triggers
  }, [measure, ...deps]);

  return { ref, isTruncated };
}
